import React, { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import Sidebar from './components/Sidebar';
import OrgView from './components/OrgView';
import TribeView from './components/TribeView';
import SaveBar from './components/SaveBar';
import LoginPage from './components/auth/LoginPage';
import AcceptInvitePage from './components/auth/AcceptInvitePage';
import { useOrgState } from './hooks/useOrgState';
import './App.css';

export type View = 'org' | 'cloud' | 'erp';

// ── Inner app (needs AuthContext) ──────────────────────────

function AppContent() {
  const { user } = useAuth();

  // Capture invite token from URL once on mount
  const [inviteToken] = useState<string | null>(
    () => new URLSearchParams(window.location.search).get('invite')
  );
  const [inviteAccepted, setInviteAccepted] = useState(false);

  function handleInviteSuccess() {
    window.history.replaceState({}, '', window.location.pathname);
    setInviteAccepted(true);
  }

  // Show accept-invite flow if token in URL and not yet accepted
  if (inviteToken && !inviteAccepted) {
    return <AcceptInvitePage token={inviteToken} onSuccess={handleInviteSuccess} />;
  }

  // Not logged in → login page
  if (!user) return <LoginPage />;

  return <MainApp />;
}

// ── Main authenticated app ─────────────────────────────────

function MainApp() {
  const [view, setView] = useState<View>('org');
  const {
    orgData, isDirty,
    updatePerson, addProcess, updateProcess, deleteProcess, updateKR,
    save, discard,
  } = useOrgState();

  const currentTribe = view !== 'org'
    ? orgData.tribes.find(t => t.id === view)
    : null;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (isDirty) save();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isDirty, save]);

  return (
    <div className="app">
      <Sidebar
        view={view}
        setView={setView}
        tribes={orgData.tribes}
        cycle={orgData.cycle}
        isDirty={isDirty}
      />
      <main className="main">
        {view === 'org' && (
          <OrgView data={orgData} setView={setView} updatePerson={updatePerson} />
        )}
        {currentTribe && (
          <TribeView
            tribe={currentTribe}
            coordinator={orgData.coordinator}
            cycle={orgData.cycle}
            updatePerson={updatePerson}
            addProcess={addProcess}
            updateProcess={updateProcess}
            deleteProcess={deleteProcess}
            updateKR={updateKR}
          />
        )}
      </main>
      {isDirty && <SaveBar onSave={save} onDiscard={discard} />}
    </div>
  );
}

// ── Root ───────────────────────────────────────────────────

export default function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}
